import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { prescriptionsService } from '@/services/prescriptions';
import { getApiError } from '@/services/api';
import type { VerifyPrescriptionResult } from '@dpfs/shared';
import {
  ArrowRight,
  CameraOff,
  CheckCircle2,
  RefreshCw,
  ScanLine,
  Search,
  ShieldAlert,
  XCircle,
} from 'lucide-react';
import { patientIdentifier } from '@/lib/patient';
import { PageHeader, Spinner } from '@/components/ui';

// dpfs://v1/<uuid>?h=<hash>&ts=<timestamp>
const QR_PATTERN = /^dpfs:\/\/v\d+\/([a-f0-9-]+)\?h=([a-f0-9]+)&ts=(\d+)$/;

type CameraError = 'denied' | 'notFound' | 'insecure' | 'generic';

function classifyCameraError(err: unknown): CameraError {
  if (typeof window !== 'undefined' && !window.isSecureContext) return 'insecure';
  const text = `${(err as { name?: string })?.name ?? ''} ${String((err as { message?: string })?.message ?? err)}`;
  if (/NotAllowed|Permission|denied/i.test(text)) return 'denied';
  if (/NotFound|no camera|Requested device not found|OverconstrainedError/i.test(text)) return 'notFound';
  return 'generic';
}

export function QRScanner() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dateLocale = i18n.language === 'ar' ? 'ar-DZ' : 'fr-DZ';
  const [verifyResult, setVerifyResult] = useState<VerifyPrescriptionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(true);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState<CameraError | null>(null);
  // Bumped to restart the camera after an error ("Retry").
  const [cameraAttempt, setCameraAttempt] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const verifyMutation = useMutation({
    mutationFn: async (url: string) => {
      const match = url.match(QR_PATTERN);
      if (!match) {
        throw new Error(t('pharmacist.scanner.invalidFormat'));
      }
      const [, id, hash, timestamp] = match;
      return prescriptionsService.verify(id!, hash!, parseInt(timestamp!, 10));
    },
    onSuccess: (data) => {
      setVerifyResult(data);
      setError(null);
    },
    onError: (err) => {
      setError(getApiError(err).message);
      setVerifyResult(null);
    },
  });

  // Keep the latest mutate in a ref so the camera effect doesn't restart on every render.
  const mutateRef = useRef(verifyMutation.mutate);
  mutateRef.current = verifyMutation.mutate;

  useEffect(() => {
    if (!isScanning || !containerRef.current) return;

    // Each run gets its own mount node so a StrictMode double-mount (or a quick
    // retry) never has two scanner instances fighting over the same element.
    const mountId = `qr-reader-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const mountNode = document.createElement('div');
    mountNode.id = mountId;
    containerRef.current.appendChild(mountNode);

    const scanner = new Html5Qrcode(mountId, {
      formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
      verbose: false,
    });
    let cancelled = false;
    let handled = false;

    setCameraReady(false);
    setCameraError(null);

    const stopScanner = () =>
      (scanner.isScanning ? scanner.stop() : Promise.resolve())
        .catch(() => undefined)
        .then(() => {
          try {
            scanner.clear();
          } catch {
            /* already cleared */
          }
          mountNode.remove();
        });

    const startPromise = scanner
      .start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: (w, h) => {
            const size = Math.floor(Math.min(w, h) * 0.7);
            return { width: Math.max(size, 150), height: Math.max(size, 150) };
          },
        },
        (decodedText) => {
          // The library can fire several times for one QR before we stop it.
          if (handled || cancelled) return;
          handled = true;
          mutateRef.current(decodedText);
          setIsScanning(false);
        },
        () => {
          // Called on every frame without a readable QR code: not an error.
        }
      )
      .then(() => {
        if (!cancelled) setCameraReady(true);
      })
      .catch((err) => {
        if (!cancelled) setCameraError(classifyCameraError(err));
      });

    return () => {
      cancelled = true;
      // Wait for start() to settle before stopping, otherwise the camera stays on.
      startPromise.finally(stopScanner);
    };
  }, [isScanning, cameraAttempt]);

  const resetScanner = () => {
    verifyMutation.reset();
    setVerifyResult(null);
    setError(null);
    setIsScanning(true);
  };

  const handleGoToFulfill = () => {
    if (verifyResult?.prescription?.id) {
      navigate(`/pharmacist/prescription/${verifyResult.prescription.id}`);
    }
  };

  const cameraErrorMessage: Record<CameraError, string> = {
    denied: t('pharmacist.scanner.cameraDenied'),
    notFound: t('pharmacist.scanner.noCamera'),
    insecure: t('pharmacist.scanner.insecure'),
    generic: t('pharmacist.scanner.cameraError'),
  };

  const rx = verifyResult?.valid ? verifyResult.prescription : undefined;
  const isMedication = !rx?.documentType || rx.documentType === 'MEDICATION';

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader
        title={t('pharmacist.scanner.title')}
        description={t('fulfillment.scanInstruction')}
        actions={
          <Link to="/pharmacist/search" className="btn-secondary">
            <Search className="h-4 w-4" />
            {t('pharmacist.scanner.manualSearch')}
          </Link>
        }
      />

      {isScanning ? (
        <div className="card overflow-hidden p-0">
          {cameraError ? (
            <div className="flex flex-col items-center px-6 py-10 text-center">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-danger-50 text-danger-600">
                <CameraOff className="h-6 w-6" />
              </div>
              <p className="font-semibold text-gray-900">{t('pharmacist.scanner.cameraUnavailable')}</p>
              <p className="mt-1 max-w-sm text-sm text-gray-500">{cameraErrorMessage[cameraError]}</p>
              <div className="mt-6 flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                <button
                  type="button"
                  onClick={() => {
                    setCameraError(null);
                    setCameraAttempt((n) => n + 1);
                  }}
                  className="btn-secondary btn-lg"
                >
                  <RefreshCw className="h-4 w-4" />
                  {t('pharmacist.common.retry')}
                </button>
                <Link to="/pharmacist/search" className="btn-primary btn-lg">
                  <Search className="h-4 w-4" />
                  {t('pharmacist.scanner.manualSearch')}
                </Link>
              </div>
            </div>
          ) : (
            <div className="relative bg-gray-900">
              <div ref={containerRef} className="mx-auto min-h-[280px] w-full max-w-md" />
              {!cameraReady && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-gray-300">
                  <Spinner className="h-8 w-8" />
                  <p className="text-sm">{t('pharmacist.scanner.cameraStarting')}</p>
                </div>
              )}
            </div>
          )}
          {!cameraError && (
            <p className="flex items-center justify-center gap-2 border-t border-gray-100 px-4 py-3 text-center text-sm text-gray-600">
              <ScanLine className="h-4 w-4 shrink-0 text-primary-600" />
              {t('pharmacist.scanner.hint')}
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {verifyMutation.isPending && (
            <div className="card flex flex-col items-center gap-3 py-10">
              <Spinner className="h-8 w-8" />
              <p className="text-gray-600">{t('fulfillment.verifying')}</p>
            </div>
          )}

          {error && (
            <div className="alert-danger">
              <XCircle className="h-5 w-5 shrink-0" />
              <div className="min-w-0">
                <h3 className="font-semibold">{t('fulfillment.invalid')}</h3>
                <p className="break-words">{error}</p>
              </div>
            </div>
          )}

          {verifyResult && !verifyResult.valid && (
            <div className="alert-danger">
              <XCircle className="h-5 w-5 shrink-0" />
              <div className="min-w-0">
                <h3 className="font-semibold">{t('fulfillment.invalid')}</h3>
                {verifyResult.error && <p className="break-words">{verifyResult.error}</p>}
                {verifyResult.fulfilledAt && (
                  <p className="mt-1 text-xs">
                    {t('fulfillment.alreadyFulfilled')}:{' '}
                    {new Date(verifyResult.fulfilledAt).toLocaleString(dateLocale)}
                  </p>
                )}
              </div>
            </div>
          )}

          {rx && (
            <div className="card overflow-hidden p-0">
              <div className="flex items-center gap-3 border-b border-success-200 bg-success-50 px-5 py-4 text-success-800">
                <CheckCircle2 className="h-6 w-6 shrink-0" />
                <h3 className="text-lg font-semibold">{t('fulfillment.valid')}</h3>
              </div>

              <div className="divide-y divide-gray-100">
                {/* Patient & prescriber */}
                <div className="grid gap-4 px-5 py-4 sm:grid-cols-2">
                  <div className="min-w-0">
                    <h4 className="section-title mb-1">{t('prescription.patient')}</h4>
                    <p className="break-words text-lg font-medium text-gray-900">
                      {rx.patient.firstName} {rx.patient.lastName}
                    </p>
                    <p className="text-sm text-gray-500">
                      {(() => {
                        const pid = patientIdentifier(rx.patient);
                        return `${t(pid.typeKey)}: ${pid.masked}`;
                      })()}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <h4 className="section-title mb-1">{t('prescription.prescriber')}</h4>
                    <p className="break-words font-medium text-gray-900">
                      {t('pharmacist.common.doctorName', {
                        name: `${rx.prescriber.firstName} ${rx.prescriber.lastName}`,
                      })}
                    </p>
                  </div>
                </div>

                {/* Medications */}
                <div className="px-5 py-4">
                  <h4 className="section-title mb-3">
                    {isMedication ? t('prescription.medications') : t('prescription.orders')}
                  </h4>
                  <ul className="space-y-3">
                    {rx.items.map((item, index) => (
                      <li key={index} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="break-words font-medium text-gray-900">
                              {item.drug ? item.drug.tradeName : item.itemLabel}
                              {item.drug?.controlledClass && (
                                <span className="badge-warning ms-2 align-middle">
                                  <ShieldAlert className="h-3 w-3" />
                                  {t('pharmacist.common.tableau', { class: item.drug.controlledClass })}
                                </span>
                              )}
                            </p>
                            {item.drug && (
                              <p className="text-sm text-gray-600">
                                {item.drug.dci} · {item.drug.strength}
                              </p>
                            )}
                          </div>
                          {item.quantity != null && (
                            <span className="badge-gray shrink-0">
                              {item.quantity} {item.quantityUnit || '×'}
                            </span>
                          )}
                        </div>
                        {item.drug && (
                          <p className="mt-1 text-sm text-gray-700">
                            {[item.dosage, item.frequency, item.duration].filter(Boolean).join(' · ')}
                          </p>
                        )}
                        {item.instructions && (
                          <p className="mt-1 break-words text-sm text-gray-500">{item.instructions}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Only medication ordonnances are dispensed */}
                <div className="bg-gray-50 px-5 py-4">
                  {isMedication ? (
                    <button type="button" onClick={handleGoToFulfill} className="btn-primary btn-lg w-full py-3">
                      {t('pharmacist.scanner.openPrescription')}
                      <ArrowRight className="h-5 w-5 rtl-flip" />
                    </button>
                  ) : (
                    <p className="text-center text-sm text-gray-600">{t('pharmacist.view.nonMedication')}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {!verifyMutation.isPending && (
            <button type="button" onClick={resetScanner} className="btn-secondary btn-lg w-full">
              <ScanLine className="h-5 w-5" />
              {t('pharmacist.scanner.scanAgain')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
